import React, { useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { trackEvent } from 'Shared/tracking/tracking';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

// Apis
import * as GoalsAPI from 'Shared/api/Goals.api';

import WpIcon from 'Wpds/components/icon/WpIcon';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import WpButton from 'Wpds/components/button/WpButton';
import { WpModal, WpModalBody, WpModalHeader, WpModalFooter } from 'Wpds/components/modal';
import { WpListSelect } from 'Wpds/components/list';
import LoadingShimmer from 'Shared/components/LoadingShimmer/LoadingShimmer';

type Props = {
  goalSlug: string;
  suggestedObjectType: 'action_item' | 'subgoal';
  handleBulkAddItems(bodies: string[]): void;
};

function GoalDetailsPageAiSuggestedSubGoalsOrActionItems({ goalSlug, handleBulkAddItems, suggestedObjectType }: Props) {
  const [isOpen, toggleIsOpen] = useToggle(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState<string[]>([]);
  const { featureFlagGoalsAiSuggestions } = useFlags();
  const { tr } = useTranslation();

  const handleCreateItemsClick = () => {
    toggleIsOpen();
    handleBulkAddItems(selectedSuggestions);
    trackEvent('Accepted AI Suggestions For Goal', { suggested_object_type: suggestedObjectType });
  };

  const generateSuggestions = () => {
    setSuggestions([]);
    setSelectedSuggestions([]);
    GoalsAPI.createSuggestedSubgoalsOrActionItems({ slug: goalSlug, suggested_object_type: suggestedObjectType }).then(
      response => {
        setSuggestions(response.data);
      }
    );
  };

  const handleGenerateSuggestionsClick = () => {
    toggleIsOpen();
    generateSuggestions();
  };

  const handleSelectedSuggestionsChange = newSelectedSuggestions => {
    setSelectedSuggestions(newSelectedSuggestions.map(s => s.value));
  };

  if (!featureFlagGoalsAiSuggestions) return null;
  return (
    <>
      <WpButton variant="tertiary" onClick={handleGenerateSuggestionsClick} size="xs" slimPadding square>
        <WpIcon name="magic" />
        {tr('perform.goals.ai_suggestions.get_suggestions')}
      </WpButton>
      <WpModal size="md" isOpen={isOpen} onToggle={toggleIsOpen}>
        <WpModalHeader>
          {suggestedObjectType === 'action_item'
            ? tr('perform.goals.ai_suggestions.header_action_items')
            : tr('perform.goals.ai_suggestions.header_sub_goals')}
        </WpModalHeader>
        <WpModalBody>
          {suggestions.length === 0 && (
            <>
              <p>
                <em>{tr('perform.goals.ai_suggestions.generating')}</em>
              </p>
              <div className="mb-2">
                <LoadingShimmer shape="rectangle" height="16px" width="90%" />
              </div>
              <div className="mb-2">
                <LoadingShimmer shape="rectangle" height="16px" width="90%" />
              </div>
              <div className="mb-2">
                <LoadingShimmer shape="rectangle" height="16px" width="90%" />
              </div>
            </>
          )}
          <WpListSelect
            id="goals-suggested-sub-items-list"
            onChange={handleSelectedSuggestionsChange}
            selectedOptions={selectedSuggestions}
            options={suggestions}
            direction="column"
          />
        </WpModalBody>
        <WpModalFooter>
          <WpButtonGroup>
            <WpButton variant="tertiary" onClick={toggleIsOpen}>
              {tr('perform.modal.buttons.cancel')}
            </WpButton>
            <WpButton disabled={selectedSuggestions.length === 0} onClick={handleCreateItemsClick}>
              {suggestedObjectType === 'action_item'
                ? tr('perform.goals.ai_suggestions.create_action_items')
                : tr('perform.goals.ai_suggestions.create_sub_goals')}
            </WpButton>
          </WpButtonGroup>
        </WpModalFooter>
      </WpModal>
    </>
  );
}

export default GoalDetailsPageAiSuggestedSubGoalsOrActionItems;
